import { Injectable, signal, inject } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { UploadDownloadService } from '@shared/services/upload-download.service';
import {
  User,
  CreateUserPayload,
  UpdateUserPayload,
} from '@shared/models/entities/users.model';

@Injectable({ providedIn: 'root' })
export class UsersService extends ApiBaseService<User> {

  private authService    = inject(AuthenticationService);
  private uploadService  = inject(UploadDownloadService);

  /**
   * Caché del usuario actual. Se rellena la primera vez que se llama loadCurrentUser().
   *
   * El `image` que se expone aquí es SIEMPRE una presigned URL fresca
   * (válida por 1h). En BD se persiste el path S3 (`user.image = 'dev/users/...'`)
   * y se resuelve al cargar al usuario, así nunca expira para el usuario
   * mientras tiene la sesión abierta.
   */
  readonly currentUser = signal<User | null>(null);

  /**
   * Path S3 ORIGINAL de la imagen del usuario actual.
   * Lo guardamos aparte para poder refrescar la presigned URL periódicamente
   * sin perder la referencia (el `currentUser().image` ya es una URL firmada
   * y no se puede usar para volver a firmar).
   */
  private currentImageKey: string | null = null;

  /**
   * Timer para refrescar la presigned URL antes de que caduque (1h).
   * Lo limpiamos en clearCurrentUser() / logout.
   */
  private imageRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super('users');
  }

  /**
   * Convierte lo que esté en `user.image` a una presigned URL fresca lista
   * para usar en <img src>.
   *
   * Casos manejados:
   *   - null/'' → null
   *   - Path S3 (no empieza con http) → llama al backend para firmar.
   *   - URL completa (http/https) → caso legado: extrae el path S3 del
   *     pathname y firma de nuevo. Si no es de nuestro bucket, devolvemos
   *     la URL tal cual.
   *
   * Cualquier error al firmar resulta en `null` (no rompemos la UI).
   */
  async resolveImageUrl(image: string | null | undefined): Promise<string | null> {
    if (!image) return null;

    let key = image.trim();

    // Si parece ser una URL, extraer la key S3 del pathname.
    if (/^https?:\/\//i.test(key)) {
      try {
        const u = new URL(key);
        // S3 path-style: <host>/<bucket>/<key>  o virtual-hosted: <bucket>.s3.../<key>
        const pathname = u.pathname.replace(/^\/+/, '');
        // Si la URL tiene el bucket en el host (virtual hosted), pathname YA es la key.
        // Si es path-style, el primer segmento es el bucket; lo quitamos.
        const isVirtualHosted = u.host.includes('s3') && u.host.split('.').length >= 3 && !pathname.startsWith('epione-hitec-documents/');
        key = isVirtualHosted ? pathname : pathname.replace(/^epione-hitec-documents\//, '');
        // Si por alguna razón la key no quedó útil, regresar el URL original.
        if (!key) return image;
      } catch {
        return image;
      }
    }

    try {
      const result = await this.uploadService.presign(key);
      return result?.url ?? null;
    } catch (err) {
      console.warn('[UsersService] No se pudo refrescar la URL de la imagen:', err);
      return null;
    }
  }

  /**
   * Crea un usuario. El backend dispara el hook que registra en Cognito
   * (envía email de invitación) y luego inserta en BD.
   */
  async createUser(payload: CreateUserPayload): Promise<User | null> {
    const body: CreateUserPayload = {
      operationType: 'single',
      ...payload,
      name:  payload.name.trim().toUpperCase(),
      email: payload.email.trim().toLowerCase(),
    };
    const response = await this.create(body as unknown as User);
    return Array.isArray(response.data) && response.data.length > 0
      ? response.data[0]
      : null;
  }

  /**
   * Actualiza un usuario. Solo viajan los campos incluidos en `payload`.
   */
  async updateUser(payload: UpdateUserPayload): Promise<User | null> {
    if (!payload.id) {
      throw new Error('updateUser requiere id.');
    }
    const body: UpdateUserPayload = {
      operationType: 'single',
      ...payload,
      ...(payload.name ? { name: payload.name.trim().toUpperCase() } : {}),
    };
    const response = await this.update(body as unknown as User);
    return Array.isArray(response.data) && response.data.length > 0
      ? response.data[0]
      : null;
  }

  /**
   * Soft-delete: marca deleted=true. El hook backend deshabilita en Cognito.
   */
  async softDeleteUser(id: string): Promise<User | null> {
    return this.updateUser({ id, deleted: true });
  }

  /**
   * Reinicia la contraseña / reenvía la invitación de un usuario.
   *
   * El backend decide automáticamente según el estado del usuario en Cognito:
   *   - Si nunca completó el primer login (FORCE_CHANGE_PASSWORD) →
   *     reenvía email de invitación con NUEVA contraseña temporal.
   *   - Si ya cambió su contraseña al menos una vez (CONFIRMED, etc.) →
   *     envía email con código para que ponga una contraseña nueva.
   *
   * Si el usuario estaba deshabilitado (p. ej. tras soft-delete), también
   * lo reactiva en Cognito antes de enviar el email.
   *
   * Devuelve { action, email, previousStatus } para mostrar mensaje contextual.
   */
  async resetPassword(
    user: { id: string; email?: string; phone?: string; name?: string }
  ): Promise<{ action: 'resend_invitation' | 'reset_password'; email: string; previousStatus?: string } | null> {
    if (!user?.id && !user?.email && !user?.phone) {
      throw new Error('resetPassword requiere id, email o teléfono.');
    }
    const body = {
      id:    user.id,
      email: user.email?.trim().toLowerCase(),
      phone: user.phone?.trim(),
      name:  user.name?.trim(),
    };
    const result = await this.request<{
      action: 'resend_invitation' | 'reset_password' | 'set_temporary_password';
      email: string;
      previousStatus?: string;
    }>('users/reset-password', 'POST', body);

    // El backend devuelve data como un objeto, no como array
    const data = result?.data as any;
    return data ?? null;
  }

  /**
   * Carga el usuario actual desde la BD usando el `sub` del JWT.
   * Cachea el resultado en el signal `currentUser`.
   *
   * Importante: el backend (generic_builder.py) lee filtros desde `condition`,
   * no del body raíz. Por eso enviamos { searchType, condition: { id } }.
   */
  async loadCurrentUser(force = false): Promise<User | null> {
    if (!force && this.currentUser()) {
      return this.currentUser();
    }

    const sub = this.authService.getCurrentUserSub();
    if (!sub) {
      this.currentUser.set(null);
      return null;
    }

    try {
      const response = await this.search({
        searchType: 'default',
        condition: { id: sub },
      });
      const user = Array.isArray(response.data) && response.data.length > 0
        ? response.data[0]
        : null;

      if (user) {
        // El `image` en BD es un path S3 (o eventualmente un URL viejo).
        // Guardamos el path original para poder refrescar la URL más adelante,
        // y resolvemos a una presigned fresca antes de exponer el user al
        // resto de la app, para que <img src> funcione inmediatamente y no
        // dependa de un reload.
        this.currentImageKey = this.extractKey(user.image);
        user.image = await this.resolveImageUrl(user.image);
        this.scheduleImageRefresh();
      }

      this.currentUser.set(user);
      return user;
    } catch (err) {
      console.error('[UsersService] No se pudo cargar el usuario actual:', err);
      this.currentUser.set(null);
      return null;
    }
  }

  /**
   * org_id (campo `organization`) del usuario actual. Lo necesita el WebSocket
   * para el ticket/broadcast. Requiere que loadCurrentUser() ya haya corrido.
   */
  getOrgId(): string | null {
    return this.currentUser()?.organization ?? null;
  }

  /**
   * Atajo para actualizar el usuario actual y mantener el caché sincronizado.
   */
  async updateCurrentUser(changes: Omit<UpdateUserPayload, 'id'>): Promise<User | null> {
    const current = this.currentUser();
    if (!current) {
      throw new Error('No hay un usuario actual cargado.');
    }
    const updated = await this.updateUser({ id: current.id, ...changes });
    if (updated) {
      // Idem que loadCurrentUser: resolvemos el path a una URL fresca
      // para que la UI vea la nueva imagen sin tener que recargar la página.
      this.currentImageKey = this.extractKey(updated.image);
      updated.image = await this.resolveImageUrl(updated.image);
      this.scheduleImageRefresh();
      this.currentUser.set(updated);
    }
    return updated;
  }

  /**
   * Extrae el path/key S3 de un valor de `image` (URL o path).
   * Usado para guardar la referencia "permanente" mientras `currentUser.image`
   * almacena la presigned URL (que cambia cada hora).
   */
  private extractKey(image: string | null | undefined): string | null {
    if (!image) return null;
    const val = image.trim();
    if (!/^https?:\/\//i.test(val)) return val;
    try {
      const u = new URL(val);
      const pathname = u.pathname.replace(/^\/+/, '');
      // Si es path-style, quitar el bucket del inicio
      return pathname.replace(/^epione-hitec-documents\//, '') || null;
    } catch {
      return null;
    }
  }

  /**
   * Programa un refresco de la presigned URL de la imagen ~55 min después,
   * justo antes de que caduque (la URL vive 1h). Re-llama a presign con la
   * key original y actualiza el signal `currentUser`. Si el usuario hace
   * logout en el camino, `clearCurrentUser` cancela el timer.
   */
  private scheduleImageRefresh(): void {
    if (this.imageRefreshTimer) {
      clearTimeout(this.imageRefreshTimer);
      this.imageRefreshTimer = null;
    }
    if (!this.currentImageKey) return;

    const REFRESH_MS = 55 * 60 * 1000; // 55 minutos
    this.imageRefreshTimer = setTimeout(async () => {
      const key = this.currentImageKey;
      const user = this.currentUser();
      if (!key || !user) return;
      try {
        const fresh = await this.uploadService.presign(key);
        if (fresh?.url) {
          this.currentUser.set({ ...user, image: fresh.url });
        }
      } catch (err) {
        console.warn('[UsersService] Falló refresco de imagen, reintentamos en 5 min:', err);
      } finally {
        // Volver a agendar
        this.scheduleImageRefresh();
      }
    }, REFRESH_MS);
  }

  /**
   * Limpia el caché del usuario actual. Llamar al hacer logout.
   * También cancela el timer de refresco de imagen.
   */
  clearCurrentUser(): void {
    if (this.imageRefreshTimer) {
      clearTimeout(this.imageRefreshTimer);
      this.imageRefreshTimer = null;
    }
    this.currentImageKey = null;
    this.currentUser.set(null);
  }

  // ─── Endpoints propios de Medtronic ─────────────────────────────────────
  // Los usan interventions, inventory, contracts y audit. Ya no queda nada
  // apuntando al `epione-core-api`: `getUserWithOrgsAndRoles()` se eliminó
  // junto con `NavigationService`, que era su único consumidor.

  /**
   * Lista usuarios, opcionalmente filtrando por NOMBRES de rol.
   *
   * Antes esto era `POST /users` con un array de nombres en el body. Ese
   * endpoint desapareció: en el API `users` ahora usa los mismos builders
   * genéricos que `roles`, así que `POST /users` CREA un usuario. La
   * búsqueda vive en `/users/search`, igual que en todas las demás
   * entidades.
   *
   * El filtro por rol se resuelve aquí: se buscan los roles por nombre y se
   * filtran los usuarios por el id resultante. Antes lo hacía el backend
   * contra `epione.users` (modelo JSONB), un esquema que ya no existe.
   *
   * Se conserva la firma y la forma de respuesta `{state, data, count}`
   * para no tocar auditoría, que es quien la usa.
   */
  public async getUsers(roles: any[] = []) {
    const response = await this.request<any>('users/search', 'POST', {
      searchType: 'default',
      condition: { deleted: false },
    });

    const roleNames = (roles ?? []).filter(Boolean);
    if (!roleNames.length || !Array.isArray(response?.data)) {
      return response;
    }

    const rolesResponse = await this.request<any>('roles/search', 'POST', {
      searchType: 'default',
      condition: { deleted: false },
    });

    const wantedIds = new Set(
      (rolesResponse?.data ?? [])
        .filter((r: any) => roleNames.includes(r?.name))
        .map((r: any) => r?.id)
    );

    // Si ninguno de los nombres pedidos existe como rol, no se filtra:
    // devolver una lista vacía rompería las pantallas que solo usan esto
    // para armar el mapa id -> nombre.
    if (!wantedIds.size) {
      return response;
    }

    const filtered = response.data.filter((u: any) => wantedIds.has(u?.role));
    return { ...response, data: filtered, count: filtered.length };
  }

  /** Obtiene un usuario por id. */
  public async getUsersById(userId: any) {
    return this.request<any>('users/id', 'POST', userId);
  }

  /** Actualiza usuarios (payload libre). Hoy equivale a `update()`. */
  public async patchUsers(users: any) {
    return this.request<any>('users', 'PATCH', users);
  }

  /** Elimina usuarios. */
  public async deleteUsers(users: any) {
    return this.request<any>('users', 'DELETE', users);
  }

  /** Nombre del rol a partir del id de usuario. */
  public async getRolNameByUserId(userId: any) {
    return this.request<any>('users/id/roles', 'POST', userId);
  }
}
